import React, { useEffect } from 'react';
import { connect } from 'react-redux';

// Components
import { PlanningAnalysisEmptyState } from './components/PlanningAnalysisEmptyState/PlanningAnalysisEmptyState';
import LoadingScreen from '@/views/components/common/LoadingScreen';

// Types
import { ReduxState } from '@/redux/modules/types';
import Nullable from '@/types/nullable';
import { Account } from '@/types/planning';
import { getComparisonItem } from '@/redux/modules/edit-portfolio';
import ComparisonItem from '@/types/comparison-item';

import User from '@/types/user';
import Locale from '@/types/locale';
import PlanningRiskProfileCompare from '../../../PlanningRiskProfileCompare';
import { getDateQuickRangeFromPreset } from '@/utils/time-range-presets';
import { TIME_RANGE_PRESET } from '@/utils/constants';
import EmptyStateCard, { EmptyStateCardSize } from '@/views/components/misc/EmptyStateCard';
import { I18N_BASE_PATH } from './constants';
import GrowthChart from '@/views/components/charts/deprecated/charts/GrowthChart';
import GenericContact from '@/types/generic-contact';
import { useContactPlanAccounts } from '../../../../../../../../hooks/planning/useContactPlanAccounts';

type DispatchProps = {
  getComparisonItem: () => Promise<ComparisonItem>;
};

type PortfolioPlanning = {
  planId: string;
  planAccountId: string;
};

type StateProps = {
  locale: Locale;
  portfolioPlanning?: PortfolioPlanning;
  user: Nullable<User>;
};

type ComponentProps = {
  onChangePlanAccount: () => void;
  contact?: GenericContact;
};
type Props = ComponentProps & DispatchProps & StateProps;

const PortfolioEditorPlanningAnalysis = ({ getComparisonItem, locale, portfolioPlanning, user, onChangePlanAccount, contact }: Props) => {
  const { comparisonItem, displayContent, isLoading, LoaderComponent, planAccount, contactHasPlan, portfolioHasAccountLinked } =
    usePortfolioEditorPlanningAnalysis({
      getComparisonItem,
      portfolioPlanning,
      contact,
    });

  if (isLoading) {
    return <div className="portfolio-editor-planning-analysis-loader">{LoaderComponent && <LoaderComponent />}</div>;
  }

  return (
    <>
      <PlanningAnalysisEmptyState
        contactHasPlan={contactHasPlan}
        portfolioHasAccount={!!portfolioHasAccountLinked}
        enterprise={user?.enterprise}
        onChangePlanAccount={onChangePlanAccount}
      />
      {displayContent && (
        <>
          {comparisonItem && planAccount && (
            <div className="portfolio-editor-planning-analysis">
              <div className="analysis-growth-chart">
                <GrowthChart
                  items={[comparisonItem]}
                  user={user}
                  localCurrency={user?.appConfig.localCurrency}
                  options={{
                    capital: comparisonItem.size,
                    targetROI: planAccount.targetRateOfReturn,
                    targetName: planAccount.name,
                    useMonthly: true,
                  }}
                  onOptionsChange={() => {}}
                  locale={locale}
                  appRegion={user?.appConfig.appRegion}
                  withAnnualizedROILegend
                  dateQuickRangeValues={getDateQuickRangeFromPreset(TIME_RANGE_PRESET.MULTI_ITEMS_DEFAULT)}
                  headerOptions={{
                    hideDateRange: true,
                    hideTargetRate: true,
                    hideCapitalAmount: true,
                    showInlineTitle: true,
                  }}
                />
              </div>
              <div className="analysis-risk-profile-compare">
                {planAccount.riskProfile ? (
                  <PlanningRiskProfileCompare account={planAccount} comparisonItem={comparisonItem} user={user} />
                ) : (
                  <div className="analysis-risk-profile-compare-empty-state">
                    <EmptyStateCard
                      titlePath={`${I18N_BASE_PATH}.MissingRiskProfile.Title`}
                      messagePath={`${I18N_BASE_PATH}.MissingRiskProfile.Message`}
                      size={EmptyStateCardSize.Small}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

type UsePortfolioEditorPlanningAnalysisProps = {
  getComparisonItem: () => Promise<ComparisonItem>;
  portfolioPlanning?: PortfolioPlanning;
  contact?: GenericContact;
};

const usePortfolioEditorPlanningAnalysis = ({ getComparisonItem, portfolioPlanning, contact }: UsePortfolioEditorPlanningAnalysisProps) => {
  const [comparisonItem, setComparisonItem] = React.useState<Nullable<ComparisonItem>>(null);
  const skip = !contact?._id;
  const { isLoading, error, selectedAccount, planId } = useContactPlanAccounts({ contact, skip, selectedPlanAccountId: portfolioPlanning?.planAccountId });

  const planNotFound = error?.status === 404 || skip;
  const contactHasPlan = !planNotFound;
  const planMismatch = planId !== portfolioPlanning?.planId;

  const displayContent = contactHasPlan && !!selectedAccount && !error && !planMismatch;
  const portfolioHasAccountLinked = portfolioPlanning?.planId && portfolioPlanning?.planAccountId;
  const LoaderComponent = isLoading ? () => <LoadingScreen titleToLocalize={`${I18N_BASE_PATH}.Loading.GetPlanAccount.Title`} /> : null;
  const planAccount = !error && !planMismatch ? selectedAccount : null;

  useEffect(() => {
    const loadComparisonItem = async () => {
      if (selectedAccount) {
        const comparisonItem = await getComparisonItem();
        setComparisonItem(comparisonItem);
      } else {
        setComparisonItem(null);
      }
    };

    loadComparisonItem();
  }, [selectedAccount, getComparisonItem]);

  return {
    comparisonItem,
    contactHasPlan,
    portfolioHasAccountLinked,
    displayContent,
    isLoading,
    LoaderComponent,
    planAccount,
  };
};

const mapStateToProp = (state: ReduxState) => ({
  locale: state.intl.locale,
  portfolioPlanning: state.editPortfolio.portfolio.planning,
  user: state.auth.user || null,
});

export default connect(mapStateToProp, {
  getComparisonItem,
})(PortfolioEditorPlanningAnalysis);
